<template>
  <div
    v-if="!loading"
    id="artwork"
    ref="single"
    v-touch:swipe="swipeHandler"
    v-touch:moving="movingHandler"
    v-touch:start="startHandler"
    v-touch:end="endHandler"
  >
    <div class="artwork-single row">
      <div class="col-md-4 order-md-1 order-2" v-if="!($isMobile() && textNote)">
        <ArtworkSidebar :artwork="artwork" />
      </div>
      <div class="col-md-8 order-md-2 order-1 art" v-if="showMainArtworkImage">
        <template>
          <ArtworkMainImage />
        </template>
      </div>
      <div class="col-md-8 order-md-2 order-1 art-notice">
        <router-view></router-view>
      </div>
    </div>
    <b-modal hide-header hide-footer lazy body-class="p-0" ref="mobileArtworkMainImage" id="mobileArtworkMainImage">
      <a href @click.prevent.stop="closeArtworkMainImageModal()" class="mobile-modal-close">
        <i class="icon-notice_close"></i>
      </a>
      <template>
        <ArtworkMainImage :autoplay="false" />
      </template>
    </b-modal>
    <ArtworkNavigation v-if="!($isMobile() && textNote)" ref="navigation" />
    <div class="art-navigation light-navigation" v-if="$isMobile() && textNote">
      <a href @click.prevent.stop="closeNote()">
        <i class="icon-CLOSE"></i>
      </a>
    </div>
    <MediaModal />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import ArtworkSidebar from './ArtworkSidebar.vue';
import ArtworkMainImage from './ArtworkMainImage.vue';
import ArtworkNavigation from './ArtworkNavigation.vue';
import MediaModal from './MediaModal.vue';
import textNoteAutoOpen from 'mixins/textNoteAutoOpenMixin.js';
import seoMetadata from 'mixins/seoMetadata';
import { publicationName, shareBaseUrl } from 'config';

export default {
  name: 'artwork',
  components: { MediaModal, ArtworkSidebar, ArtworkMainImage, ArtworkNavigation },
  mixins: [textNoteAutoOpen, seoMetadata],
  computed: {
    ...mapGetters('artworks', ['getById', 'getElementById', 'artwork', 'loading', 'elements']),
    ...mapState('route', ['query', 'params']),
    ...mapState('textNotes', ['textNote']),
    isNonSeparable() {
      return this.artwork.type === 'nonseparable';
    },
    isSeparable() {
      return this.artwork.type === 'separable';
    },
    showMainArtworkImage() {
      return !this.textNote && !(this.$isMobile() && this.isSeparable) && !this.swiped;
    },
  },
  methods: {
    ...mapMutations('artworks', ['assign', 'loaded']),
    ...mapActions('artworks', ['read']),
    closeNote() {
      const query = Object.assign({}, this.$route.query, { note: null });
      this.$router.push({ name: 'artwork', query });
    },
    assignImages() {
      if (this.isNonSeparable) {
        this.assign({ name: 'images', data: this.artwork.imageSet.headers });
      } else {
        this.assign({ name: 'images', data: this.artwork.imageSet.images });
      }
      this.assign({ name: 'currentImage', data: 0 });
    },
    closeArtworkMainImageModal() {
      this.$root.$emit('bv::hide::modal', 'mobileArtworkMainImage');
    },
    swipeHandler(direction) {
      if (!this.$isMobile()) {
        return;
      }

      if (direction === 'left') {
        this.direction = 'slideOutLeft';
        this.swiped = true;
        this.startX = null;
        this.$refs.navigation.next();
        return;
      }

      if (direction === 'right') {
        this.direction = 'slideOutRight';
        this.swiped = true;
        this.startX = null;
        this.$refs.navigation.previous();
        return;
      }

      this.startX = null;
      this.$refs.single.style.transform = '';
    },
    movingHandler(e) {
      if (!this.$isMobile()) {
        return;
      }

      let { clientX, clientY } = e.changedTouches[0];
      let diffY = Math.round(clientY - this.startY);
      let diffX = Math.round(clientX - this.startX);

      if (Math.abs(diffX) > Math.abs(diffY)) {
        this.$refs.single.style.transform = `translateX(${diffX}px)`;
      }
    },
    startHandler(e) {
      if (!this.$isMobile()) {
        return;
      }

      this.startX = e.changedTouches[0].clientX;
      this.startY = e.changedTouches[0].clientY;
    },
    endHandler(e) {
      if (!this.$isMobile()) {
        return;
      }

      this.startX = null;
      this.startY = null;
      this.$refs.single.style.transform = '';
    },
  },
  async created() {
    const id = this.$route.params.id;
    const artwork = this.getById(id);

    if (artwork) this.assign({ name: 'artwork', data: artwork });
    else await this.read({ id });

    this.loaded();
  },
  watch: {
    textNote(newVal, oldVal) {
      if (newVal) {
        const { note } = this.$route.params;
        if (!note || note !== newVal.id) {
          const query = Object.assign({}, this.$route.query);
          const noteId = newVal.slug ?? newVal.id;
          this.$router.replace({ name: 'ArtworkNote', query, params: { note: noteId } }).catch((_) => {});
        }
      }
    },
    artwork(newValue, oldValue) {
      if (newValue) {
        const { note, id } = this.params;
        const { slug } = newValue;
        const parsedNoteId = note?.split('-').reverse()[0];
        const textNote = this.getTextNoteById(newValue.text_notes, parsedNoteId);
        const parsedtextNoteId = textNote?.slug || textNote?.id || note;
        this.$store.commit('artworks/assign', { name: 'artworkId', data: newValue.id });

        this.$store.commit('params/SET_SILENT', true);
        this.$router
          .replace({
            query: { ...this.query },
            params: { id: slug, ...(note && { note: parsedtextNoteId }) },
          })
          .catch(() => {});

        this.assignImages();

        if (note) {
          if (!this.hasNote(note)) {
            this.$router.replace({ name: 'artwork', query: { ...this.query }, params: { id, note: null } });
            return;
          }
        } else {
          this.autoOpenTextNote();
        }
      }
    },
    swiped(newValue, oldValue) {
      if (newValue) {
        this.$refs.single.classList.add('animated', this.direction);
        this.$refs.single.addEventListener('animationend', () => {
          this.$refs.single.classList = '';
          this.$refs.single.style.transform = '';
          this.swiped = false;
        });
      }
    },
    $route(to, from) {
      const { id, silent } = to.params;
      const { isHeader } = to.query;
      if (silent) {
        return;
      }
      if (isHeader) {
        this.assign({ name: 'images', data: this.artwork.imageSet.headers });
        this.assign({ name: 'currentImage', data: 0 });
      } else if (this.isSeparable) {
        this.assign({ name: 'images', data: this.artwork.imageSet.images });
        this.assign({ name: 'currentImage', data: 0 });
      }

      if (id !== from.params.id) {
        const artwork = this.getById(id);
        if (artwork) this.assign({ name: 'artwork', data: artwork });
        else this.read({ id });
      }
    },
  },
  data() {
    return {
      direction: null,
      swiped: null,
      startX: null,
      startY: null,
    };
  },
  metaInfo() {
    return {
      ...this.metaConfig({
        title: `${this.metaArtworkAuthor} - ${this.metaArtworkTitle}`,
        titleTemplate: this.metaTitleTemplate,
        description: `
        ${this.metaArtworkTitle}. ${this.metaArtworkAuthor}, ${this.metaArtworkDate}.
        ${this.metaArtworkDescription}.
        ${publicationName}
      `,
      }),
      link: [
        {
          vmid: 'canonical',
          rel: 'canonical',
          href: `${shareBaseUrl}${this.$route.path.substring(1)}`,
        },
      ],
    };
  },
};
</script>

<style lang="less">
.art {
  background-color: white;
}

.stretch-content {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

#mobileArtworkMainImage {
  a.mobile-modal-close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;

    i {
      line-height: 40px;
      font-size: 40px;
      color: black;
    }
  }
}
</style>
